import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { TaskForAsyncTaskFunction } from 'ember-concurrency';
import { task } from 'ember-concurrency';
import type EmployeeModel from 'garaje/models/employee';
import type FloorModel from 'garaje/models/floor';
import { createURN } from 'garaje/models/generic-resource';
import type AjaxService from 'garaje/services/ajax';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type StateService from 'garaje/services/state';
import type { ResourceAmenityType } from 'garaje/types/generic-resources/ResourceTypeConfig';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import type { BulkResourceUpdatePayload } from 'garaje/utils/map/api/bulk-update-resources';
import { postGenericResources } from 'garaje/utils/map/api/bulk-update-resources';

import { ADD_SPOTS_OPTION } from '../add-parking-options/component';
import type { StepContext } from '../ui/classic/multi-step-modal/component';

interface ResourceFormFields {
  [key: string]: string | boolean | string[] | number[] | null;
}

interface AddParkingFormArgs {
  resource?: {
    name?: string;
    floorId?: string;
    assignments?: { employeeId?: string }[];
    amenities?: { id: string }[];
    geometry?: {
      pointGeometry?: {
        coordinates?: number[];
      };
    };
  };
  floors: FloorModel[];
  amenities: ResourceAmenityType[];
  amenitiesMap: { [key: string]: string };
  employees: EmployeeModel[];
  hasMoreEmployeePages: boolean;
  addSpotsOption: ADD_SPOTS_OPTION;
  loadMoreEmployeesTask: TaskForAsyncTaskFunction<unknown, () => Promise<void>>;
  onEmployeeSearchTask: TaskForAsyncTaskFunction<unknown, (term: string) => Promise<void>>;
  stepContext?: StepContext;
  onClose?: () => void;
  onSave?: () => void;
  onFormDataChange?: (formData: ResourceFormFields) => void;
  onSaveTask?: TaskForAsyncTaskFunction<unknown, () => Promise<void>>;
}

export default class AddParkingFormComponent extends Component<AddParkingFormArgs> {
  @service declare store: StoreService;
  @service declare state: StateService;
  @service declare ajax: AjaxService;
  @service declare flashMessages: FlashMessagesService;

  @tracked formFields: ResourceFormFields = {
    name: '',
    floor: null,
    assignedEmployeeId: null,
    amenityIds: [],
    coordinates: [],
  };

  @tracked employeeError: string[] = [];
  @tracked nameError: string[] = [];
  @tracked quantity: number = 1;
  @tracked startingSuffixNumber: number = 1;
  @tracked isResettingForm: boolean = false;

  constructor(owner: unknown, args: AddParkingFormArgs) {
    super(owner, args);

    if (this.args.resource) {
      this.formFields = {
        name: this.args.resource.name ?? '',
        floor: this.args.resource.floorId?.toString() ?? null,
        assignedEmployeeId: this.args.resource.assignments?.[0]?.employeeId?.toString() ?? null,
        amenityIds: (this.args.resource.amenities ?? []).map((a) => a.id.toString()),
        coordinates: this.args.resource.geometry?.pointGeometry?.coordinates ?? [],
      };
    }

    // Register actions for this step if stepContext is provided
    if (this.args.stepContext) {
      this.registerActions();
    }

    // Notify parent of initial form data if callback is provided
    this.args.onFormDataChange?.(this.formFields);
  }

  get isSaveTaskRunning(): boolean {
    return this.handleSaveTask.isRunning;
  }

  get isSaveAndAddAnotherTaskRunning(): boolean {
    return this.handleSaveAndAddAnotherTask.isRunning;
  }

  get isAnySaveTaskRunning(): boolean {
    return this.isSaveTaskRunning || this.isSaveAndAddAnotherTaskRunning;
  }

  get selectedEmployee(): undefined | { id: string; name: string } {
    const employeeId = this.formFields['assignedEmployeeId'] as string | null;
    if (!employeeId) {
      return;
    }

    const employee = this.store.peekRecord('employee', employeeId);
    if (!employee) {
      return { id: employeeId, name: employeeId };
    }

    return { id: employeeId, name: employee.name };
  }

  get selectedAmenities(): ResourceAmenityType[] {
    const amenityIds = (this.formFields['amenityIds'] as string[]) || [];
    return this.args.amenities.filter((amenity) => amenityIds.includes(amenity.resourceTypeAmenityId.toString()));
  }

  get selectedFloor(): { id: string; name: string } | null {
    const floorId = this.formFields['floor'] as string;
    if (!floorId) {
      return null;
    }

    const floor = this.args.floors.find((floor: FloorModel) => floor.id === floorId);
    if (!floor) {
      return null;
    }
    return { id: floor.id, name: floor.name };
  }

  get hasFormError(): boolean {
    return !!(this.nameError.length || this.employeeError.length);
  }

  get isFormValid(): boolean {
    const { name, floor } = this.formFields;
    return !!(name && floor && !this.hasFormError);
  }

  @action
  updateButtonStates(): void {
    const isValid = this.isFormValid;
    const isAnyTaskRunning = this.isAnySaveTaskRunning;

    // If we're resetting the form, force the buttons to their normal state
    if (this.isResettingForm) {
      if (this.args.stepContext) {
        this.registerActions();
      }
      return;
    }

    // Update actions with current state if stepContext is provided
    if (this.args.stepContext) {
      this.args.stepContext.registerActions({
        primary: {
          label: isAnyTaskRunning ? 'Saving...' : 'Save',
          action: this.handleSaveTask,
          isTask: true,
          disabled: isAnyTaskRunning || !isValid,
        } as const,
        secondary: {
          label: isAnyTaskRunning
            ? ''
            : this.args.addSpotsOption === ADD_SPOTS_OPTION.BULK
              ? 'Save and add more'
              : 'Save and add another',
          action: this.handleSaveAndAddAnotherTask,
          isTask: true,
          disabled: isAnyTaskRunning || !isValid,
        } as const,
      });
    }
  }

  @action
  registerActions(): void {
    if (this.args.stepContext) {
      this.args.stepContext.registerActions({
        primary: {
          label: 'Save',
          action: this.handleSaveTask,
          isTask: true,
          disabled: !this.isFormValid,
        } as const,
        secondary: {
          label: this.args.addSpotsOption === ADD_SPOTS_OPTION.BULK ? 'Save and add more' : 'Save and add another',
          action: this.handleSaveAndAddAnotherTask,
          isTask: true,
          disabled: !this.isFormValid,
        } as const,
      });
    }
  }

  @action
  updateField(field: string, value: string): void {
    this.formFields = { ...this.formFields, [field]: value };
    this.validateField(field);
    this.updateButtonStates();

    // Notify parent of form data changes if callback is provided
    this.args.onFormDataChange?.(this.formFields);
  }

  @action
  validateField(field: string): void {
    if (field === 'name') {
      this.validateNameField();
    }
    this.updateButtonStates();
  }

  @action
  validateNameField(): void {
    const name = this.formFields['name'] as string;

    if (isEmpty(name)) {
      this.nameError = ['Please enter a parking name'];
    } else if (name.length > 40) {
      this.nameError = ['Parking name must be 40 characters or less'];
    } else {
      this.nameError = [];
    }
  }

  @action
  handleSelectFloor(floor?: FloorModel): void {
    if (!floor) {
      this.formFields = { ...this.formFields, floor: null };
    } else {
      this.formFields = { ...this.formFields, floor: floor.id };
    }

    this.updateButtonStates();
    this.args.onFormDataChange?.(this.formFields);
  }

  clearEmployeeErrors(): void {
    this.employeeError = [];
    this.updateButtonStates();
  }

  @action
  handleEmployeeSelect(employee: EmployeeModel | 'loadMore' | null): void {
    if (!employee) {
      this.clearEmployeeErrors();
      this.formFields = { ...this.formFields, assignedEmployeeId: null };
      this.args.onFormDataChange?.(this.formFields);
      return;
    }

    if (employee === 'loadMore') {
      return;
    }

    this.clearEmployeeErrors();
    this.formFields = { ...this.formFields, assignedEmployeeId: employee.id };
    this.args.onFormDataChange?.(this.formFields);
  }

  @action
  updatedSelectedAmenities(selectedAmenities: ResourceAmenityType[]): void {
    this.formFields = {
      ...this.formFields,
      amenityIds: selectedAmenities.map((amenity) => amenity.resourceTypeAmenityId.toString()),
    };
    this.args.onFormDataChange?.(this.formFields);
  }

  @action
  updateQuantity(value: string): void {
    let num = parseInt(value, 10);
    if (isNaN(num) || num < 0) num = 0;
    this.quantity = num;
  }

  @action
  updateStartingSuffixNumber(value: string): void {
    let num = parseInt(value, 10);
    if (isNaN(num)) num = 1;
    this.startingSuffixNumber = num;
  }

  @action
  handleSearchFloors(searchTerm: string): FloorModel[] {
    return this.args.floors.filter((floor) => floor.name.toLowerCase().includes(searchTerm.toLowerCase()));
  }

  @action
  handleEmployeeSearch(searchTerm: string): EmployeeModel[] {
    // Perform the search task
    void this.args.onEmployeeSearchTask.perform(searchTerm);

    // Return filtered options based on search term
    if (!searchTerm) {
      return this.args.employees;
    }

    return this.args.employees.filter((employee) => employee.name.toLowerCase().includes(searchTerm.toLowerCase()));
  }

  @action
  handleLoadMoreEmployees(): void {
    if (this.args.hasMoreEmployeePages) {
      void this.args.loadMoreEmployeesTask.perform();
    }
  }

  createNewResourceTask = task(
    { restartable: true },
    async (
      formData: {
        name: string;
        floor: string;
        assignedEmployeeId?: string;
        amenityIds?: string[];
        enabled: boolean;
        quantity?: number;
        startingSuffixNumber?: number;
      },
      shouldCloseModal: boolean = true,
    ) => {
      const payload: BulkResourceUpdatePayload[] = [];
      const quantity = formData.quantity ?? 1;
      const startSeq = formData.startingSuffixNumber ?? 1;
      const name = formData.name;

      for (let i = 0; i < quantity; i++) {
        payload.push({
          op: 'ADD',
          data: {
            name: quantity > 1 ? `${name} ${startSeq + i}` : name,
            floorId: parseInt(formData.floor),
            resourceTypeId: 2,
            amenities: formData.amenityIds?.map((amenityId) => ({ id: parseInt(amenityId) })) || [],
            assignments: formData.assignedEmployeeId ? [{ employeeId: parseInt(formData.assignedEmployeeId) }] : [],
            relationId: createURN('location', this.state.currentLocation.id),
            geometry: {
              type: 'Point',
              coordinates: [],
            },
          },
        });
      }

      try {
        await postGenericResources(this.ajax, payload);

        if (shouldCloseModal) {
          this.args.onClose?.();
        }

        this.flashMessages.showAndHideFlash('success', 'Parking spot saved');
      } catch (error) {
        this.flashMessages.showFlash('error', parseErrorForDisplay(error));
      }
    },
  );

  handleSaveTask = task({ drop: true }, async () => {
    // Update button states when task starts
    this.updateButtonStates();

    // If an external save task is provided, use it instead
    if (this.args.onSaveTask) {
      await this.args.onSaveTask.perform();
      return;
    }

    const formData = {
      name: this.formFields['name'] as string,
      floor: this.formFields['floor'] as string,
      assignedEmployeeId: this.formFields['assignedEmployeeId'] as string,
      amenityIds: this.formFields['amenityIds'] as string[],
      enabled: true,
      quantity: this.quantity,
      startingSuffixNumber: this.startingSuffixNumber,
    };

    try {
      await this.createNewResourceTask.perform(formData, true);
    } finally {
      // Update button states when task completes
      this.updateButtonStates();
    }
  });

  handleSaveAndAddAnotherTask = task({ drop: true }, async () => {
    // Update button states when task starts
    this.updateButtonStates();

    try {
      await this.createNewResourceTask.perform(
        {
          name: this.formFields['name'] as string,
          floor: this.formFields['floor'] as string,
          assignedEmployeeId: this.formFields['assignedEmployeeId'] as string,
          amenityIds: this.formFields['amenityIds'] as string[],
          enabled: true,
          quantity: this.quantity,
          startingSuffixNumber: this.startingSuffixNumber,
        },
        false,
      );
    } finally {
      // Set flag to indicate we're resetting the form
      this.isResettingForm = true;

      // Update button states when task completes
      this.updateButtonStates();

      // Reset form after task is fully complete
      this.resetForm();

      // Clear the reset flag
      this.isResettingForm = false;
    }
  });

  resetForm(): void {
    this.formFields = {
      name: '',
      floor: null,
      assignedEmployeeId: null,
      amenityIds: [],
    };
    this.nameError = [];
    this.employeeError = [];
    this.quantity = 1;
    this.startingSuffixNumber = 1;
  }
}
